<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import TheIcon from '@/Components/TheIcon.vue'
import { Link } from '@inertiajs/vue3'
import { RoomType } from '@/Pages/the-home/enums/Search'
import { HeaderSearchResultEmitType } from '@/Components/the-header-search-result/enums/HeaderSearchResultEmitType'
import DateRangePicker from '@/Components/DateRangePicker.vue'
import RoomTypeSwitcher from '@/Components/RoomTypeSwitcher.vue'
import SearchCounter from '@/Components/SearchCounter.vue'
import { vOnClickOutside } from '@vueuse/components'

import {
    changeRoomType,
    getAvailability,
    dateRangeComparePrices,
    groupAgeRangesComparePrices,
    groupTypeComparePrices,
    guestsComparePrices,
    guestsQuantityComparePrices,
    inputDateRangeComparePrices,
    isGroupTypesVisibleComparePrices,
    isLoading,
    nightsComparePrices,
    roomsComparePrices,
    roomsQuantityComparePrices,
    roomTypeComparePrices,
    skipDateComparePrices,
    nights,
    ageRangesValidationPrice
} from '@/Composables/comparePriceLogic'
import { isLoading as isLoadingHeader } from '@/Composables/use-listing-search'
import GroupTypesSelect from '@/Components/GroupTypesSelect.vue'
import AgeRangesSelect from '@/Components/AgeRangesSelect.vue'
import { useMobile } from '@/Composables/mobile'
import ComparePriceCard from './ComparePriceCard.vue'
import { Image, LoadingData, Room } from '../interfaces/listing'
import TheLoading from '@/Pages/the-city/components/TheLoading.vue'
import AppModal from '@/Components/the-modal/AppModal.vue'
import { ContactActiveImports } from '../interfaces/contactActiveImports'
import { isValidAgeRangesSelect, toggleIsValidAgeRangesSelect } from '@/Composables/TheHeaderLogic'
import { User } from '@/types'
import { HiddenGemzType } from '@/Interfaces/Common'
import HiddenGemz from '@/Components/hidden-gemz/HiddenGemz.vue'
import MaxNightBanner from '@/Pages/the-city/components/MaxNightBanner.vue'
import { activeCurrency, showCurrencyAndScrollTop } from '@/Composables/use-currency'
import { AdData } from '@/Pages/the-city/interfaces/City'
import TheCityAds from '@/Pages/the-city/components/AdFeatures/TheCityAds.vue'

type Props = {
    availability: Room[]
    loading: LoadingData[]
    cityUrl: string
    hostelName: string
    cityName: string
    countryName: string
    listingImages: Image[]
    listingIdentifier: number,
    hostelIsClosed: boolean,
    bookHostelsHref: string | undefined,
    pageContactActiveImports: ContactActiveImports,
    user: User | null,
    hiddenGemz?: HiddenGemzType[]
    adForListing?: AdData
}

const props = defineProps<Props>()

const comparePriceData = ref(props.availability)

const emit = defineEmits(['tabsSwitchAndScroll'])

watch(() => props.availability, (newAvailability) => {
    comparePriceData.value = newAvailability
})

const isLocationVisible = ref(false)
const isDatepickerVisible = ref(false)
const isRoomTypeVisible = ref(false)
const isGuestsCounterVisible = ref(false)
const isRoomsCounterVisible = ref(false)

const { isMobile } = useMobile()

const mappingEmitTypeToReference = {
    [HeaderSearchResultEmitType.LOCATION]: isLocationVisible,
    [HeaderSearchResultEmitType.DATES]: isDatepickerVisible,
    [HeaderSearchResultEmitType.ROOM_TYPE]: isRoomTypeVisible,
    [HeaderSearchResultEmitType.GUESTS]: isGuestsCounterVisible,
    [HeaderSearchResultEmitType.ROOMS]: isRoomsCounterVisible
}

async function expand (value: HeaderSearchResultEmitType): Promise<void> {
    if (skipDateComparePrices.value) {
        skipDateComparePrices.value = false
    }

    if (mappingEmitTypeToReference[value].value === true) {
        mappingEmitTypeToReference[value].value = false
        return
    }

    collapseAllOptions()

    if (mappingEmitTypeToReference[value]) {
        mappingEmitTypeToReference[value].value = true
    }
}

function collapseAllOptions (): void {
    for (const key in mappingEmitTypeToReference) {
        mappingEmitTypeToReference[key as keyof typeof mappingEmitTypeToReference].value = false
    }
}

function submit (): void {
    if (!ageRangesValidationPrice()) {
        return
    }

    const request = {
        url: '/listing-booking-search/',
        listingID: props.listingIdentifier
    }

    getAvailability(request, (data: Room[]) => {
        comparePriceData.value = data
    })
}

const switchRoomType = (): void => {
    changeRoomType(roomTypeComparePrices.value === RoomType.PRIVATE ? RoomType.DORM : RoomType.PRIVATE)

    submit()
}

const isEmptyOrNotListed = computed(() => {
    const keys = Object.keys(props.pageContactActiveImports)

    let allListedAreFalse = true

    if (keys.length === 0) {
        return true
    }

    keys.forEach(key => {
        if (props.pageContactActiveImports[key].isListed) {
            allListedAreFalse = false
        }
    })

    return allListedAreFalse
})

</script>

<template>
    <div v-if="!hostelIsClosed" class="compare-prices-tab">
        <div class="">
            <p class="text-base font-weight-600">
                Compare Prices and Availability from Hostelworld, Booking, and More
            </p>
            <p class="text-sm">
                Prices and availability vary between platforms. At Hostelz, we show you all rooms, beds, and prices side by side from the major booking portals. Save money and book the hostel you really want.
            </p>
        </div>
        <div class="d-flex flex-column-reverse flex-lg-row align-items-xl-center justify-content-between mb-5">
            <div
                class="d-inline-flex compare-prices-tab-control-panel-wrap justify-content-center"
            >
                <div class=" search-dropdown-wrap bookingSearchRoomType flex-shrink-0 order-1 order-xl-0">
                    <div
                        class="compare-prices-tab-room-type-switcher-wrap"
                    >
                        <RoomTypeSwitcher
                            v-model="roomTypeComparePrices"
                            @update:model-value="submit"
                        />
                    </div>
                </div>

                <div
                    class="d-flex align-items-center compare-prices-tab-control-panel-inner flex-wrap flex-shrink-0 order-1 order-xl-0"
                >
                    <div
                        v-on-click-outside="() => isDatepickerVisible = false"
                        class="position-relative compare-prices-tab-control-panel-calendar"
                    >
                        <button
                            class="d-flex align-items-center btn-clear p-0 "
                            :class="{ 'dropdown-toggle-btn--active': isDatepickerVisible }"
                            @click="expand(HeaderSearchResultEmitType.DATES)"
                        >
                            <TheIcon
                                icon-id="calendar"
                                width="24"
                                height="24"
                                class="mr-2"
                            />

                            <span class="compare-prices-tab-control-panel-text tx-small">  {{
                                inputDateRangeComparePrices || 'Add dates'
                            }}, {{ nightsComparePrices }} </span>
                        </button>

                        <div
                            v-if="isDatepickerVisible"
                            class="dropdown-menu compare-prices-tab-control-panel-calendar-dropdown p-4 d-lg-block d-none"
                        >
                            <DateRangePicker
                                v-model="dateRangeComparePrices"
                                @dates-selected="expand(HeaderSearchResultEmitType.DATES)"
                                @update:model-value="submit"
                            />
                        </div>

                        <AppModal
                            :show="isDatepickerVisible && isMobile"
                            class="compare-prices-tab-modal"
                            :hidden-close-icon="true"
                            :is-disabled="true"
                            @close="isDatepickerVisible = false"
                        >
                            <div
                                class="dropdown-menu compare-prices-tab-control-panel-calendar-dropdown p-4 "
                            >
                                <DateRangePicker
                                    v-model="dateRangeComparePrices"
                                    @dates-selected="expand(HeaderSearchResultEmitType.DATES)"
                                    @update:model-value="submit"
                                />
                            </div>
                        </AppModal>
                    </div>

                    <div
                        v-on-click-outside="() => isGuestsCounterVisible = false"
                        class="position-relative compare-prices-tab-control-panel-guests"
                    >
                        <button
                            class="d-flex align-items-center btn-clear p-0 "
                            :class="{ 'dropdown-toggle-btn--active': isGuestsCounterVisible }"
                            @click="expand(HeaderSearchResultEmitType.GUESTS)"
                        >
                            <TheIcon
                                icon-id="guests-icon"
                                width="24"
                                height="24"
                                class="mr-2"
                            />

                            <span class="compare-prices-tab-control-panel-text tx-small flex-shrink-0"> {{
                                guestsComparePrices
                            }}</span>

                            <span v-if="!isValidAgeRangesSelect" class="text-danger">*</span>
                        </button>

                        <div
                            v-if="isGuestsCounterVisible"
                            class="dropdown-menu p-3 p-lg-5 compare-prices-tab-control-panel-guests-dropdown"
                        >
                            <div>
                                <div
                                    class="d-flex flex-column justify-content-center align-items-start bk-search__wrap"
                                >
                                    <SearchCounter
                                        v-model="guestsQuantityComparePrices"
                                        label="Guests"
                                        @update:model-value="submit"
                                    />
                                </div>

                                <div v-if="isGroupTypesVisibleComparePrices">
                                    <GroupTypesSelect v-model="groupTypeComparePrices" @update:model-value="submit" />

                                    <AgeRangesSelect
                                        v-model="groupAgeRangesComparePrices"
                                        :is-valid="isValidAgeRangesSelect"
                                        @set-is-valid-age-ranges-select-to-default="toggleIsValidAgeRangesSelect"
                                        @update:model-value="submit"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="roomTypeComparePrices === RoomType.PRIVATE"
                        v-on-click-outside="() => isRoomsCounterVisible = false"
                        class="position-relative compare-prices-tab-control-panel-rooms"
                    >
                        <button
                            class="d-flex align-items-center btn-clear  p-0 "
                            :class="{ 'dropdown-toggle-btn--active': isRoomsCounterVisible }"
                            @click="expand(HeaderSearchResultEmitType.ROOMS)"
                        >
                            <TheIcon
                                icon-id="rooms"
                                width="24"
                                height="24"
                                class="mr-2"
                            />

                            <span class="compare-prices-tab-control-panel-text tx-small">{{ roomsComparePrices }}</span>
                        </button>

                        <div
                            v-if="isRoomsCounterVisible"
                            class="dropdown-menu compare-prices-tab-control-panel-rooms-dropdown p-3 p-lg-5"
                        >
                            <div class="searchRoomsOptions">
                                <div
                                    class="d-flex flex-column justify-content-center align-items-start bk-search__wrap"
                                >
                                    <SearchCounter
                                        v-model="roomsQuantityComparePrices"
                                        label="Rooms"
                                        @update:model-value="submit"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        class="compare-prices-tab-control-panel-currency"
                        @click="showCurrencyAndScrollTop"
                    >
                        <span>{{ `${ activeCurrency.prefix } ${ activeCurrency.suffix }` }}</span>
                    </div>
                </div>
            </div>

            <div v-if="adForListing" class="d-inline-block">
                <TheCityAds
                    :url="adForListing.url"
                    :image="adForListing.image"
                    :content="adForListing.content"
                    class="mb-lg-0 mb-3"
                    @tabs-switch-and-scroll="emit('tabsSwitchAndScroll', $event)"
                />
            </div>

            <div v-if="!adForListing && hiddenGemz && hiddenGemz.length" class="d-inline-block">
                <HiddenGemz
                    v-for="(hg, key) in hiddenGemz"
                    :key
                    :="hg"
                    :user="user"
                    :is-single-listing="true"
                    class="p-3 mb-lg-0 mb-3"
                    @tabs-switch-and-scroll="emit('tabsSwitchAndScroll', $event)"
                />
            </div>  
        </div>
        

        <TheLoading
            v-if="isLoading || isLoadingHeader"
            :items="loading"
        />

        <div v-else>
            <MaxNightBanner />

            <div
                v-if="!isEmptyOrNotListed && comparePriceData.length > 0"
                class="compare-prices-tab-card-wrap d-flex flex-wrap"
            >
                <ComparePriceCard
                    v-for="(item, index) in comparePriceData"
                    :key="`${item.title}-${index}`"
                    :availability="item"
                    :nights="nights"
                    :listing-images="listingImages"
                />
            </div>

            <div v-else-if="!isEmptyOrNotListed && comparePriceData.length === 0" id="bookingSearchResult">
                <div class="bookingSearchStatusBox">
                    <div class="text-center">
                        <p>
                            Sorry, {{ hostelName }} has no available
                            {{ roomTypeComparePrices === RoomType.DORM ? 'dorm beds' : 'private rooms' }} for your
                            dates.
                        </p>

                        <p>
                            Try different dates or <span
                                class="switchToDormSearch btn btn-primary px-2 my-2 cursor-pointer"
                                @click="switchRoomType"
                            >Check {{ roomTypeComparePrices !== RoomType.DORM ? 'dorm beds' : 'private rooms' }}
                                at {{ hostelName }}</span>
                        </p>

                        <p class="text-center mt-4">
                            <Link
                                title="Check more hostels in Amsterdam for availability"
                                class="btn btn-lg btn-outline-primary bg-primary tt-n py-2 px-sm-5 font-weight-600 rounded text-white"
                                :href="cityUrl"
                            >
                                Compare all hostels in {{ cityName }}
                            </Link>
                        </p>
                    </div>
                </div>
            </div>

            <div v-else-if="isEmptyOrNotListed" class="alert noLinksForBooking text-center mb-4">
                <div class="text-center">
                    <h2 class="hero-heading h3">
                        Oh no, no online bookings here!
                    </h2>
                    <p class="text-dark">
                        This accommodation isn’t available for online booking through any of the major hostel platforms. Explore other fantastic hostels in {{ cityName }} to find the perfect place for your stay.
                    </p>

                    <p class="text-center mt-4">
                        <Link
                            :title="`Explore more hostels in ${cityName} for availability`"
                            class="btn btn-lg btn-outline-primary bg-primary tt-n py-2 px-sm-5 font-weight-600 rounded text-white"
                            :href="cityUrl"
                        >
                            Find Hostels in {{ cityName }}
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    </div>
    <div v-else class="compare-prices-tab-hostel-is-closed">
        <h3 class="mb-5">
            Sadly, this hostel is closed.
        </h3>

        <p v-if="bookHostelsHref" class="mb-4">
            On the bright side, there are <a
                rel="nofollow"
                target="_blank"
                :href="bookHostelsHref"
                :title="`more hostels in ${cityName }`"
            >
                more hostels {{ cityName ? `in ${ cityName }` : '' }}
            </a>
        </p>
        <Link
            v-if="cityUrl"
            class="btn btn-lg btn-outline-primary bg-primary tt-n py-2 px-sm-5 font-weight-600 rounded text-white"
            :href="cityUrl"
            :title="`Let's find your Hostel in ${ cityName }!`"
        >
            Let's find your Hostel in {{ cityName }}!
        </Link>
    </div>
</template>

<style lang="scss" scoped>
.compare-prices-tab {

    &:deep(.hidden-gemz) {
        @media (min-width: 992px) {
            max-width: 455px;
        }

        @media (min-width: 1200px) {
            max-width: 505px;
        }

        .hg-content h3 {
            font-size: 18px;
            margin-bottom: 16px;

            @media (min-width: 576px) {
                font-size: 20px;
            }

            @media (min-width: 992px) {
                font-size: 18px;
            }
        }

        .hg-btn {
            font-size: 12px;
        }
    }

    &:deep(.asidebarcity) {
        @media (min-width: 992px) {
            max-width: 455px;
        }

        @media (min-width: 1200px) {
            max-width: 505px;
        }

        .anu_container {
            overflow: hidden;
        }

        .anu_content {
            padding: 10px;
        }

        .anu_title {
            font-size: 18px;
            margin-bottom: 4px;
            line-height: 1.2;
        }

        .anu_text {
            font-size: 12px;
            margin-bottom: 6px;
            line-height: 1.2;
        }

        .anu_button {
            font-size: 14px;
            padding: 10px 12px;
        }
        .anu_promo_code{
            font-size: 12px;
            padding: 6px 12px;
        }
        .anu_promo_code_meta{
            display: none !important;
        }

        @media (max-width: 768px) {
            .anu_image{
                height: 120px !important;
                flex: initial !important;
                min-height: initial !important;
            }
             .anu_title {
                font-size: 22px;
            }
        }
        .anu_image img {
            height: 100%;
            width: 100%;
            object-fit: cover;
        }
    }

    &-control-panel {
        &-wrap {
            gap: 16px;
            flex-direction: column;
            align-items: start;

            @media (min-width: 992px) {
                gap: 32px;
            }

            @media (min-width: 1400px) {
                flex-direction: row;
                align-items: center;
            }
        }

        &-inner {
            gap: 16px;

            @media (min-width: 768px) {
                gap: 16px;
            }
        }

        &-text {
            text-decoration: underline;
        }

        &-rooms {
            width: 100px;

            &-dropdown {
                display: block;
                right: auto;
                left: 0;

                @media (min-width: 462px) {
                    right: 15%;
                    left: auto;
                }

                @media (min-width: 576px) {
                    right: auto;
                    left: 0;
                }
            }
        }

        &-guests {
            width: 100px;

            &-dropdown {
                display: block;
                right: auto;
                left: 0;

                @media (min-width: 346px) {
                    right: 15%;
                    left: auto;
                }

                @media (min-width: 576px) {
                    right: auto;
                    left: 0;
                }
            }
        }

        &-calendar {
            width: fit-content;

            &-dropdown {
                display: block;
                transform: translate(-50%, -50%);

                @media (min-width: 992px) {
                    transform: translate(0, 0);
                    left: -50%;
                }

                @media (min-width: 1200px) {
                    left: 0;
                }

                &:deep(.dp__flex_display) {
                    flex-direction: column;
                    @media (min-width: 640px) {
                        flex-direction: row;
                    }
                }
            }
        }

        &-currency {
            cursor: pointer;
            background-color: var(--blue-10);
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 20px;
            min-width: 34px;
            height: 34px;
            padding: 5px 12px;
            white-space: nowrap;
        }
    }

    &-card {
        &-wrap {
            gap: 16px;
        }
    }

    &-room-type-switcher {
        &-wrap {
            background-color: var(--accent-bg);
            border-radius: 8px;

            &:deep(.hz-room-type-switcher-title) {
                display: none;
            }
        }
    }

    &-hostel-is-closed {
        display: flex;
        flex-direction: column;
        align-items: center;
    }
}

.hostelIsClosed {
    background-color: #ff733d;
    transition: background-color 0.3s;

    &:hover {
        background-color: #cc5c30;
        border-color: #cc5c30;
    }
}

.noLinksForBooking {
    background-color: #f8f9fa;
}
</style>

<style>
.compare-prices-tab {

    .modal-open {
        overflow: auto;
    }
}

.compare-prices-tab-modal {
    .slide-fade-enter-active, .slide-fade-leave-active {
        transition: transform 0.4s ease-out, opacity .5s ease-out;

        .app-modal-dialog {
            transition: transform 0.4s ease-out, opacity .5s ease-out;
        }
    }

    .slide-fade-enter-from {
        opacity: 0;

        .app-modal-dialog {
            transform: translateY(0px);
            opacity: 0;
        }
    }

    .slide-fade-enter-to {
        opacity: 1;

        .app-modal-dialog {
            transform: translateY(0);
            opacity: 1;
        }
    }

    .slide-fade-leave-from {
        opacity: 1;

        .app-modal-dialog {
            transform: translateY(0);
            opacity: 1;
        }
    }

    .slide-fade-leave-to {
        opacity: 0;

        .app-modal-dialog {
            transform: translateY(-60px);
            opacity: 0;
        }
    }
}
</style>

